<template>
  <div class="account-modern">
    <NavBar v-if="navBar" />
    <AuthBySessionExpired />
    <ChangePassword />
    <ChangeGeolocation :user_id="id" :geolocation="geolocation" @save="get" />
    <SecurityQuestions />

    <div class="account-container">
      <!-- Header Section -->
      <div class="account-header">
        <div class="header-content">
          <div class="back-button-section">
            <v-btn-previous-page />
          </div>
          <div class="title-section">
            <h1 class="page-title">
              <i class="fas fa-user-circle title-icon"></i>
              Minha Conta
            </h1>
            <p class="page-subtitle">
              Gerencie suas informações pessoais e configurações
            </p>
          </div>
          <div class="actions-section">
            <v-btn
              v-if="permission('read', 'permission')"
              @click="handleToPermissions"
              class="btn-permissions-modern"
            >
              <i class="fas fa-shield-alt mr-2"></i>
              Permissões
            </v-btn>
          </div>
        </div>
      </div>

      <b-overlay :show="loading" opacity="0.99" class="loading-overlay">
        <div class="account-content mb-5">
          <!-- Profile Card -->
          <div class="profile-section">
            <div class="profile-card">
              <b-overlay
                :show="loadingImage"
                opacity="0.99"
                class="image-overlay"
              >
                <!-- Profile Image Section -->
                <div class="profile-image-section">
                  <div class="profile-avatar-container">
                    <img
                      :src="
                        photo_url ? photo_url : require('../assets/user.png')
                      "
                      class="profile-avatar"
                      alt="Foto do perfil"
                    />
                    <div class="avatar-overlay">
                      <i class="fas fa-camera"></i>
                    </div>
                  </div>

                  <div class="profile-image-actions">
                    <span
                      class="btn-upload-modern"
                      v-if="permission('edit', 'resident_photo')"
                    >
                      <i
                        :class="photo_url ? 'fas fa-sync-alt' : 'fas fa-camera'"
                        class="mr-2"
                      ></i>
                      {{ photo_url ? "Alterar Foto" : "Adicionar Foto" }}
                      <input
                        type="file"
                        @change="updateProfilePic($event.target.files[0])"
                        class="file-input"
                        ref="button_img"
                        accept="image/*"
                      />
                    </span>

                    <v-btn
                      v-if="photo_url"
                      class="btn-remove-photo"
                      @click="deleteProfilePic"
                      small
                    >
                      <i class="fas fa-trash mr-1"></i>
                      Remover
                    </v-btn>
                  </div>

                  <div class="image-info">
                    <p class="info-text">
                      <i class="fas fa-info-circle mr-1"></i>
                      <span class="mr-1">Máximo:</span>
                      <strong>10MB</strong>
                    </p>
                    <p class="info-text">
                      <span class="mr-1">Formatos:</span>
                      <strong>JPG, PNG</strong>
                    </p>
                  </div>

                  <ErrorsMessages
                    v-if="new_profile_pic_errors.length > 0"
                    :errors="new_profile_pic_errors"
                  />
                </div>
              </b-overlay>
            </div>
          </div>

          <!-- Information Cards Section -->
          <div class="info-section">
            <ErrorsMessages :errors="errors" />

            <!-- Personal Information Card -->
            <div class="info-card">
              <div class="card-header">
                <h3 class="card-title">
                  <i class="fas fa-user mr-2"></i>
                  Informações Pessoais
                </h3>
              </div>
              <div class="card-content">
                <div class="info-grid">
                  <div class="info-item full-width">
                    <label class="info-label">Nome Completo</label>
                    <p class="info-value">{{ name }}</p>
                  </div>

                  <div class="info-item">
                    <label class="info-label">CPF</label>
                    <p class="info-value">{{ cpf }}</p>
                  </div>

                  <div class="info-item">
                    <label class="info-label">E-mail</label>
                    <p class="info-value">{{ email }}</p>
                  </div>

                  <div class="info-item">
                    <label class="info-label">Status</label>
                    <span class="status-badge status-active">
                      <i class="fas fa-check-circle mr-1"></i>
                      {{ status_type_name }}
                    </span>
                  </div>

                  <div class="info-item">
                    <label class="info-label">Grupo</label>
                    <p class="info-value">{{ user_group_name }}</p>
                  </div>

                  <div class="info-item">
                    <label class="info-label">Perfil</label>
                    <p class="info-value">{{ user_profile_name }}</p>
                  </div>

                  <div v-if="has_coren" class="info-item">
                    <label class="info-label">COREN</label>
                    <p class="info-value">
                      {{ coren.uf }} {{ coren.code }} {{ coren.category }}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Account Details Card -->
            <div class="info-card">
              <div class="card-header">
                <h3 class="card-title">
                  <i class="fas fa-clock mr-2"></i>
                  Detalhes da Conta
                </h3>
              </div>
              <div class="card-content">
                <div class="info-grid">
                  <div class="info-item">
                    <label class="info-label">Data de Criação</label>
                    <p class="info-value">
                      <i class="fas fa-calendar-plus mr-1"></i>
                      {{ formatDate(created_at) }}
                    </p>
                  </div>

                  <div class="info-item">
                    <label class="info-label">Última Alteração</label>
                    <p class="info-value">
                      <i class="fas fa-edit mr-1"></i>
                      {{ formatDate(updated_at) }}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Offline Sync Warning -->
            <div v-if="showSyncUpdateOffline" class="warning-card">
              <div class="warning-content">
                <i class="fas fa-exclamation-triangle warning-icon"></i>
                <div class="warning-text">
                  <h4>Sincronização Offline</h4>
                  <p>
                    Configure suas questões de segurança para acesso offline
                    neste dispositivo.
                  </p>
                </div>
              </div>
            </div>

            <!-- Actions Card -->
            <div class="actions-card">
              <div class="card-header">
                <h3 class="card-title">
                  <i class="fas fa-cogs mr-2"></i>
                  Configurações da Conta
                </h3>
              </div>
              <div class="card-content">
                <div class="actions-grid">
                  <v-btn
                    class="action-btn security-btn"
                    @click="$bvModal.show('security_questions')"
                  >
                    <i class="fas fa-shield-alt mr-2"></i>
                    <div class="btn-text">
                      <span class="btn-title">Questões de Segurança</span>
                      <span class="btn-subtitle">Acesso offline</span>
                    </div>
                  </v-btn>

                  <v-btn
                    class="action-btn password-btn"
                    @click="$bvModal.show('change_password')"
                  >
                    <i class="fas fa-lock mr-2"></i>
                    <div class="btn-text">
                      <span class="btn-title">Alterar Senha</span>
                      <span class="btn-subtitle">Segurança da conta</span>
                    </div>
                  </v-btn>

                  <v-btn
                    v-if="permission('read', 'geolocation')"
                    class="action-btn location-btn"
                    @click="$bvModal.show('change_geolocation')"
                  >
                    <i class="fas fa-map-marker-alt mr-2"></i>
                    <div class="btn-text">
                      <span class="btn-title">Geolocalização</span>
                      <span class="btn-subtitle">Configurar localização</span>
                    </div>
                  </v-btn>
                </div>
              </div>
            </div>
          </div>
        </div>
      </b-overlay>
    </div>
  </div>
</template>

<script>
import NavBar from "../templates/NavBar";
import ErrorsMessages from "../templates/Components/ErrorsMessages";
import AuthBySessionExpired from "../templates/Components/AuthBySessionExpired";
import ChangePassword from "../templates/Account/ChangePassword";
import ChangeGeolocation from "../templates/Account/ChangeGeolocation";
import SecurityQuestions from "../templates/Account/SecurityQuestions";

import api from "../services/api";

import toast from "../lib/ToastNotification";

import { formatDate, onlyCodeCoren } from "../lib/format";

import routes from "../routes";

import { userData } from "../lib/functions";

import permission from "../lib/permission";

import { set } from "idb-keyval";

import VBtnPreviousPage from "../templates/Components/VBtnPreviousPage.vue";

export default {
  name: "Account",
  components: {
    NavBar,
    AuthBySessionExpired,
    ErrorsMessages,
    ChangePassword,
    ChangeGeolocation,
    SecurityQuestions,
    VBtnPreviousPage,
  },
  data() {
    return {
      showSyncUpdateOffline: false,
      navBar: true,
      user: {},
      new_profile_pic: null,
      new_profile_pic_errors: [],
      rest_home_id: "",
      id: "",
      name: "",
      cpf: "",
      user_profile_name: "",
      user_group_name: "",
      status_type_name: "",
      coren: {},
      has_coren: false,
      email: "",
      geolocation: null,
      created_at: "",
      updated_at: "",
      photo_url: "",
      loading: true,
      loadingImage: false,
      errors: [],
    };
  },
  created() {
    this.retrieve();
  },
  methods: {
    permission,
    formatDate,
    onlyCodeCoren,

    async retrieve() {
      let { user } = userData();

      this.id = user.id;
      this.rest_home_id = user.rest_home_id;

      let { question_answer } = routes.currentRoute.query;

      if (this.errors.length == 0) await this.get();
      if (this.errors.length == 0) await this.getOfflineAccess();
      if (this.errors.length > 0) return;

      if (question_answer) {
        setTimeout(() => this.$bvModal.show("security_questions"), 200);
      }
    },

    async getOfflineAccess() {
      try {
        const response = await api.get("/rest-home/users/offline-access");
        const { users } = response.data;

        let {
          user: { email },
        } = this;

        let user = users.find((user) => user.email == email);

        if (!user) {
          this.showSyncUpdateOffline = true;
        } else {
          this.showSyncUpdateOffline = false;
        }
      } catch (error) {
        console.log(error);
      }
    },

    async get() {
      try {
        this.loading = true;

        const response = await api.get("/rest-home/user/self");

        const { user, error, errors } = response.data;

        if (error) {
          this.errors = errors;
        } else {
          this.user = user;
          this.name = user.name;
          this.email = user.email;
          this.cpf = user.cpf;
          this.user_profile_name = user.user_profile_name;
          this.user_group_name = user.user_group_name;
          this.status_type_name = user.status_type_name;
          this.geolocation = user.geolocation;
          this.created_at = user.created_at;
          this.updated_at = user.updated_at;
          this.photo_url = user.photo_url;

          var coren = onlyCodeCoren(user.professional_class_council_value);

          if (coren) {
            this.coren = coren;
            if (user.professional_class_council_value) {
              this.has_coren = true;
            } else {
              this.has_coren = false;
            }
          }

          this.errors = [];
        }

        this.loading = false;
      } catch (error) {
        console.log(error);
      }
    },

    async updateProfilePic(image) {
      try {
        if (!image) {
          return;
        }

        this.loadingImage = true;

        let photo = image;
        if (photo.size > 10 * 1024 * 1024) {
          this.new_profile_pic_errors.push({
            id: this.new_profile_pic_errors.length,
            message: "A imagem deve ter até 10MB",
          });
          this.new_profile_pic = null;
          return;
        }

        let formData = new FormData();
        formData.append("photo", photo, photo.name);
        let response = await api.put("/rest-home/user/self/picture", formData);
        let { error, errors, message } = response.data;

        if (error) {
          this.new_profile_pic_errors = errors;
        } else {
          this.new_profile_pic = null;
          this.new_profile_pic_errors = [];
          await this.get();
          this.updateNavBar();
          toast(message, "success");
        }
        this.loadingImage = false;
      } catch (error) {
        console.log(error);
      }
    },

    updateNavBar() {
      sessionStorage.setItem("user", JSON.stringify(this.user));
      this.navBar = false;
      setTimeout(() => (this.navBar = true), 100);
    },

    async deleteProfilePic() {
      try {
        this.loadingImage = true;

        const response = await api.delete("/rest-home/user/self/picture");
        const { error, message, errors } = response.data;

        if (error) {
          this.new_profile_pic_errors = errors;
        } else {
          this.new_profile_pic_errors = [];
          await this.get();
          this.updateNavBar();
          toast(message, "success");
        }

        this.loadingImage = false;
      } catch (error) {
        console.log(error);
      }
    },

    async update() {
      try {
        this.loading = true;

        const { name } = this;

        let params = { name };

        const response = await api.patch("/rest-home/user/update", params);

        const { error, errors, message, user } = response.data;

        if (error) {
          this.errors = errors;
        } else {
          toast(message, "success");
          this.errors = [];
          await set("user", user);
          sessionStorage.setItem("rest_home_id", user.rest_home_id);
          sessionStorage.setItem("rest_home_name", user.rest_home_name);
          sessionStorage.setItem("user", JSON.stringify(user));
        }
        this.loading = false;
      } catch (error) {
        console.log(error);
      }
    },

    handleToPermissions() {
      routes.push({
        name: "permissions",
        params: {
          profile_id: this.user_profile_id,
          profile_name: this.name,
        },
      });
    },
  },
};
</script>

<style scoped>
.account-modern {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

.account-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

/* Header Section */
.account-header {
  padding: 24px 0;
  background: white;
  border-radius: 0 0 24px 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  margin-bottom: 32px;
}

.header-content {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 24px;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

.page-title {
  font-size: 32px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  font-family: "WorkSans-SemiBold", sans-serif;
}

.title-icon {
  color: #0f65b0;
  margin-right: 12px;
}

.page-subtitle {
  color: #64748b;
  margin: 4px 0 0 0;
  font-size: 16px;
}

.btn-permissions-modern {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 12px !important;
  padding: 12px 24px !important;
  font-weight: 600 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  box-shadow: 0 4px 15px rgba(245, 158, 11, 0.3) !important;
  transition: all 0.3s ease !important;
}

.btn-permissions-modern:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 6px 20px rgba(245, 158, 11, 0.4) !important;
}

/* Content Layout */
.account-content {
  display: grid;
  grid-template-columns: 350px 1fr;
  gap: 32px;
  align-items: start;
}

/* Profile Section */
.profile-card {
  background: white;
  border-radius: 20px;
  padding: 32px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  position: sticky;
  top: 24px;
}

.profile-image-section {
  text-align: center;
}

.profile-avatar-container {
  position: relative;
  display: inline-block;
  margin-bottom: 24px;
}

.profile-avatar {
  width: 150px;
  height: 150px;
  border-radius: 50%;
  object-fit: cover;
  border: 4px solid #0f65b0;
  box-shadow: 0 8px 32px rgba(15, 101, 176, 0.2);
  transition: all 0.3s ease;
}

.profile-avatar:hover {
  transform: scale(1.05);
}

.avatar-overlay {
  position: absolute;
  bottom: 8px;
  right: 8px;
  background: #0f65b0;
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 3px solid white;
  cursor: pointer;
  transition: all 0.3s ease;
}

.avatar-overlay:hover {
  background: #1e40af;
  transform: scale(1.1);
}

.profile-image-actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
}

.btn-upload-modern {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #0f65b0 0%, #1e40af 100%);
  color: white;
  padding: 12px 24px;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  box-shadow: 0 4px 15px rgba(15, 101, 176, 0.3);
  overflow: hidden;
}

.btn-upload-modern:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(15, 101, 176, 0.4);
}

.file-input {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
}

.btn-remove-photo {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 12px !important;
  font-weight: 600 !important;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.3) !important;
  height: 48px !important;
}

.btn-remove-photo:hover {
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4) !important;
}

.image-info {
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
}

.info-text {
  color: #6b7280;
  font-size: 12px;
  margin: 4px 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Information Cards */
.info-section {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.info-card,
.actions-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  overflow: hidden;
}

.card-header {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
}

.card-title {
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
}

.card-title i {
  color: #0f65b0;
  margin-right: 8px;
}

.card-content {
  padding: 24px;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.info-item.full-width {
  grid-column: 1 / -1;
}

.info-label {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0;
}

.info-value {
  font-size: 14px;
  font-weight: 500;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
}

.info-value i {
  color: #6b7280;
  margin-right: 4px;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-active {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  color: #059669;
  border: 1px solid #34d399;
}

/* Warning Card */
.warning-card {
  background: linear-gradient(135deg, #fef3c7 0%, #fed7aa 100%);
  border: 1px solid #f59e0b;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 4px 15px rgba(245, 158, 11, 0.1);
}

.warning-content {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.warning-icon {
  color: #d97706;
  font-size: 24px;
  margin-top: 4px;
}

.warning-text h4 {
  color: #92400e;
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 4px 0;
}

.warning-text p {
  color: #92400e;
  font-size: 14px;
  margin: 0;
}

/* Actions Grid */
.actions-grid {
  display: grid;
  gap: 16px;
}

.action-btn {
  display: flex !important;
  align-items: center !important;
  padding: 20px !important;
  border-radius: 12px !important;
  border: 2px solid transparent !important;
  text-align: left !important;
  font-weight: 500 !important;
  transition: all 0.3s ease !important;
  background: #f8fafc !important;
  color: #374151 !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05) !important;
  width: 100% !important;
  justify-content: flex-start !important;
}

.action-btn:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1) !important;
}

.security-btn:hover {
  border-color: #10b981 !important;
  background: #ecfdf5 !important;
}

.password-btn:hover {
  border-color: #f59e0b !important;
  background: #fffbeb !important;
}

.location-btn:hover {
  border-color: #8b5cf6 !important;
  background: #f5f3ff !important;
}

.action-btn i {
  font-size: 20px !important;
  width: 24px !important;
  margin-right: 16px !important;
}

.security-btn i {
  color: #10b981 !important;
}
.password-btn i {
  color: #f59e0b !important;
}
.location-btn i {
  color: #8b5cf6 !important;
}

.btn-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.btn-title {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
}

.btn-subtitle {
  font-size: 12px;
  color: #6b7280;
}

/* Loading Overlays */
.loading-overlay,
.image-overlay {
  border-radius: 16px;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .account-content {
    grid-template-columns: 1fr;
    gap: 24px;
  }

  .profile-card {
    position: static;
  }
}

@media (max-width: 768px) {
  .header-content {
    grid-template-columns: 1fr;
    gap: 16px;
    text-align: center;
  }

  .account-container {
    padding: 0 16px;
  }

  .page-title {
    font-size: 24px;
    justify-content: center;
  }

  .profile-card {
    padding: 24px;
  }

  .profile-avatar {
    width: 120px;
    height: 120px;
  }

  .info-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .actions-grid {
    gap: 12px;
  }

  .action-btn {
    padding: 16px !important;
  }
}

@media (max-width: 480px) {
  .card-content {
    padding: 16px;
  }

  .card-header {
    padding: 16px;
  }

  .card-title {
    font-size: 16px;
  }

  .btn-text {
    gap: 1px;
  }

  .btn-title {
    font-size: 13px;
  }

  .btn-subtitle {
    font-size: 11px;
  }
}
</style>